import React, { createContext, useContext, useState, useEffect } from 'react';

export const CMSContext = createContext();

export const useCMS = () => useContext(CMSContext);

export const CMSProvider = ({ children }) => {
  const [content, setContent] = useState({});
  const [settings, setSettings] = useState({});
  const [assets, setAssets] = useState({});
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);

  // AUTOMATIC DETECTOR: Uses cPanel URL on the live web, and localhost during development
  const isProduction = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
  
  // Update the backend domain path inside the quotes if your Flask backend uses a different subdomain (e.g., 'https://eunikareinternational.org')
  const BACKEND_BASE_URL = process.env.REACT_APP_BACKEND_URL || (isProduction 
    ? 'https://eunikareinternational.org' 
    : 'http://127.0.0.1:5000');

  const API_URL = process.env.REACT_APP_API_URL || `${BACKEND_BASE_URL}/api`;

  const fetchAllData = async () => {
    try {
      const [contentRes, settingsRes, assetsRes, pagesRes] = await Promise.all([
        fetch(`${API_URL}/content`),
        fetch(`${API_URL}/settings`),
        fetch(`${API_URL}/assets`),
        fetch(`${API_URL}/pages`)
      ]);

      if (contentRes.ok) setContent(await contentRes.json());
      if (settingsRes.ok) {
        const settingsData = await settingsRes.json();
        setSettings(settingsData);
        Object.entries(settingsData).forEach(([key, value]) => {
          if (value && (key.startsWith('brand-') || key.startsWith('font-'))) {
            document.documentElement.style.setProperty(`--${key}`, value);
          }
        });
      }
      if (assetsRes.ok) setAssets(await assetsRes.json());
      if (pagesRes.ok) setPages(await pagesRes.json());
    } catch (err) {
      console.error("Error fetching CMS data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, [API_URL]);

  const resolveImage = (key, fallback) => {
    if (assets[key]) {
      return `${BACKEND_BASE_URL}/static/uploads/${assets[key]}`;
    }
    return fallback;
  };

  const getPageData = (slug) => {
    const page = pages.find(p => p.slug === slug);
    if (!page) return null;
    
    // Map sections by type for easy access
    const sectionsMapped = {};
    page.sections.forEach(s => {
      sectionsMapped[s.section_type] = s.content;
    });
    
    return { ...page, sectionData: sectionsMapped };
  };

  const value = {
    content,
    settings,
    assets,
    pages,
    loading,
    API_URL,
    resolveImage,
    getPageData,
    refresh: fetchAllData
  };

  return <CMSContext.Provider value={value}>{children}</CMSContext.Provider>;
};
